/**
 * Imperative bridge between the UI surfaces (sticky bar, drawer,
 * immersive view, lyrics panel) and the hidden global `<audio>` engine.
 *
 * The store holds *state* (current track, queue, isPlaying, volume…).
 * The controller pushes *commands* at the audio element + dispatches
 * store actions. UI never touches `<audio>` directly — it always goes
 * through the controller so the play state stays consistent across
 * every entry point.
 *
 * Engine registration: GlobalMusicEngine calls `registerEngine` with
 * the HTMLAudioElement ref on mount and `null` on unmount. Calls made
 * before an engine registers are no-ops (the user can't trigger any
 * action before the React tree mounts).
 */
import { useMusicPlayerStore, type MiniPlayerTrack } from '@/lib/music-player-store';

// The engine element is `<audio>` in music mode and `<video>` in karaoke
// mode (so the burnt-in hardsub track plays). Both extend HTMLMediaElement
// — the controller only uses play/pause/seek/volume which live on the
// shared interface, so a wider type lets us swap the engine cleanly.
let engineRef: HTMLMediaElement | null = null;

export const playerController = {
  registerEngine(media: HTMLMediaElement | null): void {
    engineRef = media;
  },

  isReady(): boolean {
    return Boolean(engineRef);
  },

  play(): void {
    if (!engineRef) return;
    engineRef
      .play()
      .catch(() => useMusicPlayerStore.getState().setPlaying(false));
  },

  pause(): void {
    engineRef?.pause();
  },

  toggle(): void {
    if (useMusicPlayerStore.getState().isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  },

  seek(time: number): void {
    if (!engineRef) return;
    if (!Number.isFinite(time) || time < 0) return;
    engineRef.currentTime = time;
    useMusicPlayerStore.getState().setCurrentTime(time);
  },

  next(): void {
    useMusicPlayerStore.getState().playNext();
  },

  previous(): void {
    useMusicPlayerStore.getState().playPrevious();
  },

  setVolume(value: number): void {
    const clamped = Math.max(0, Math.min(1, value));
    if (engineRef) {
      engineRef.volume = clamped;
      engineRef.muted = clamped === 0;
    }
    useMusicPlayerStore.getState().setVolume(clamped);
  },

  toggleMute(): void {
    const state = useMusicPlayerStore.getState();
    const nextMuted = !state.isMuted;
    if (engineRef) engineRef.muted = nextMuted;
    useMusicPlayerStore.getState().setMuted(nextMuted);
  },
};

/**
 * Public entry point UI components use to start playback. If the queue
 * is empty (or only contains the same track) it plays immediately;
 * otherwise the play-action modal opens to ask whether to replace the
 * queue, play next, or just enqueue.
 */
export function requestPlayTrack(
  track: MiniPlayerTrack,
  options?: { queue?: MiniPlayerTrack[]; startIndex?: number }
): void {
  const state = useMusicPlayerStore.getState();
  const sameTrack = state.currentTrack?.id === track.id;
  const queueEmpty = !state.currentTrack || !state.isVisible;

  if (queueEmpty || sameTrack) {
    if (sameTrack && !queueEmpty) {
      playerController.play();
      return;
    }
    state.playTrack(track, options);
    return;
  }

  state.requestPlay({
    track,
    queue: options?.queue,
    startIndex: options?.startIndex,
  });
}

/**
 * Play an entire queue starting at index 0. Used by "Play all" buttons
 * — they shouldn't trigger the action modal even if something else is
 * already playing, the intent is "replace queue, full stop".
 */
export function playQueueNow(
  queue: MiniPlayerTrack[],
  startIndex = 0
): void {
  if (queue.length === 0) return;
  useMusicPlayerStore.getState().playTrack(queue[startIndex] || queue[0], {
    queue,
    startIndex,
  });
}
